import { useEffect, useState, useCallback } from 'react'

export interface AntiCheatState {
    strikes: number
    isFullscreen: boolean
    violations: string[]
}

interface UseAntiCheatOptions {
    maxStrikes?: number
    onMaxStrikes?: () => void
    onViolation?: (violation: string) => void
}

export function useAntiCheat(options: UseAntiCheatOptions = {}) {
    const { maxStrikes = 3, onMaxStrikes, onViolation } = options

    const [state, setState] = useState<AntiCheatState>({
        strikes: 0,
        isFullscreen: false,
        violations: [],
    })

    const addViolation = useCallback((violation: string) => {
        setState(prev => {
            const newStrikes = prev.strikes + 1
            const newState = {
                strikes: newStrikes,
                isFullscreen: prev.isFullscreen,
                violations: [...prev.violations, violation],
            }

            // Call violation callback
            onViolation?.(violation)

            // Check if max strikes reached
            if (newStrikes >= maxStrikes) {
                setTimeout(() => onMaxStrikes?.(), 500)
            }

            return newState
        })
    }, [maxStrikes, onMaxStrikes, onViolation])

    // Fullscreen handlers
    const enterFullscreen = useCallback(async () => {
        try {
            await document.documentElement.requestFullscreen()
        } catch (error) {
            console.error('Failed to enter fullscreen:', error)
        }
    }, [])

    const handleFullscreenChange = useCallback(() => {
        const isCurrentlyFullscreen = !!document.fullscreenElement
        setState(prev => ({ ...prev, isFullscreen: isCurrentlyFullscreen }))

        if (!isCurrentlyFullscreen && state.strikes < maxStrikes) {
            addViolation('Exited fullscreen mode')
        }
    }, [addViolation, maxStrikes, state.strikes])

    // Visibility change handler (tab switching)
    const handleVisibilityChange = useCallback(() => {
        if (document.hidden && state.strikes < maxStrikes) {
            addViolation('Switched to another tab')
        }
    }, [addViolation, maxStrikes, state.strikes])

    // Window blur handler (switching windows)
    const handleWindowBlur = useCallback(() => {
        if (state.strikes < maxStrikes) {
            addViolation('Switched to another window')
        }
    }, [addViolation, maxStrikes, state.strikes])

    // Keyboard shortcuts prevention
    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        // Prevent common cheating shortcuts
        const isCheatingShortcut =
            (e.ctrlKey && (e.key === 'c' || e.key === 'v' || e.key === 'x')) || // Copy/Paste/Cut
            (e.ctrlKey && e.shiftKey && e.key === 'I') || // DevTools
            e.key === 'F12' || // DevTools
            (e.ctrlKey && e.key === 'u') || // View Source
            (e.ctrlKey && e.key === 's') || // Save
            (e.altKey && e.key === 'Tab') // Alt+Tab

        if (isCheatingShortcut) {
            e.preventDefault()
            if (state.strikes < maxStrikes) {
                addViolation('Attempted restricted keyboard shortcut')
            }
        }
    }, [addViolation, maxStrikes, state.strikes])

    // Context menu prevention (right-click)
    const handleContextMenu = useCallback((e: MouseEvent) => {
        e.preventDefault()
    }, [])

    // Setup event listeners
    useEffect(() => {
        document.addEventListener('fullscreenchange', handleFullscreenChange)
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('blur', handleWindowBlur)
        document.addEventListener('keydown', handleKeyDown)
        document.addEventListener('contextmenu', handleContextMenu)

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('blur', handleWindowBlur)
            document.removeEventListener('keydown', handleKeyDown)
            document.removeEventListener('contextmenu', handleContextMenu)
        }
    }, [handleFullscreenChange, handleVisibilityChange, handleWindowBlur, handleKeyDown, handleContextMenu])

    // Auto-enter fullscreen on mount
    useEffect(() => {
        enterFullscreen()
    }, [enterFullscreen])

    return {
        ...state,
        enterFullscreen,
        addViolation,
        isMaxStrikesReached: state.strikes >= maxStrikes,
    }
}
